import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { verificarEmailService } from "../services/auth/verificarEmail.service";

// ---------------------------------------------------------------------------
// Estados posibles
// ---------------------------------------------------------------------------
const STATE = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR_PARAMS: "error_params",
  ERROR_SERVER: "error_server",
  ERROR_NETWORK: "error_network",
};

// ---------------------------------------------------------------------------

const VerificarEmailLayer = () => {
  const [searchParams] = useSearchParams();
  const [estado, setEstado] = useState(STATE.LOADING);
  const [mensaje, setMensaje] = useState("");

  useEffect(() => {
    const verificar = async () => {
      const uid = searchParams.get("uid");
      const token = searchParams.get("token");

      // 1. Validar parámetros
      if (!uid || !token) {
        setEstado(STATE.ERROR_PARAMS);
        setMensaje("Link inválido. Solicita un nuevo registro.");
        return;
      }

      // 2. Llamar al backend
      try {
        const data = await verificarEmailService({ uid, token });
        setEstado(STATE.SUCCESS);
        setMensaje(data.mensaje || "¡Correo verificado con éxito!");
      } catch (error) {
        if (error.response) {
          // El backend respondió con error (400, etc.)
          setEstado(STATE.ERROR_SERVER);
          setMensaje(
            error.response.data?.error ||
              error.response.data?.detail ||
              "El link expiró o no es válido."
          );
        } else {
          // Error de red
          setEstado(STATE.ERROR_NETWORK);
          setMensaje("Error de conexión. Intenta de nuevo más tarde.");
        }
      }
    };

    verificar();
  }, [searchParams]);

  // ---------------------------------------------------------------------------
  // Pantalla de carga
  // ---------------------------------------------------------------------------
  if (estado === STATE.LOADING) {
    return (
      <section className="auth bg-base d-flex flex-wrap">
        <div className="auth-left d-lg-block d-none">
          <div className="d-flex align-items-center flex-column h-100 justify-content-center">
            <img src="assets/images/auth/auth-img.png" alt="" />
          </div>
        </div>
        <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
          <div className="max-w-464-px mx-auto w-100 text-center">
            <div className="spinner-border text-primary mb-16" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Verificando...</span>
            </div>
            <h4 className="mb-8">Verificando tu correo</h4>
            <p className="text-secondary-light">Estamos confirmando tu dirección de correo electrónico...</p>
          </div>
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Pantalla de éxito
  // ---------------------------------------------------------------------------
  if (estado === STATE.SUCCESS) {
    return (
      <section className="auth bg-base d-flex flex-wrap">
        <div className="auth-left d-lg-block d-none">
          <div className="d-flex align-items-center flex-column h-100 justify-content-center">
            <img src="assets/images/auth/auth-img.png" alt="" />
          </div>
        </div>
        <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
          <div className="max-w-464-px mx-auto w-100 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-16"
              style={{
                width: 80,
                height: 80,
                background: "rgba(16,185,129,0.12)",
              }}
            >
              <Icon icon="mdi:email-check-outline" width="44" style={{ color: "#10b981" }} />
            </div>
            <h4 className="mb-8">¡Correo verificado con éxito!</h4>
            <p className="text-secondary-light mb-24">{mensaje || "Ya puedes iniciar sesión en MisTalentos."}</p>
            <Link to="/sign-in" className="btn btn-primary px-32 py-12 radius-12">
              Iniciar sesión
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Pantallas de error (params faltantes, error del servidor, error de red)
  // ---------------------------------------------------------------------------
  const esErrorRed = estado === STATE.ERROR_NETWORK;

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src="assets/images/auth/auth-img.png" alt="" />
        </div>
      </div>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100 text-center">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-16"
            style={{
              width: 80,
              height: 80,
              background: "rgba(239,68,68,0.12)",
            }}
          >
            <Icon icon="mdi:email-remove-outline" width="44" style={{ color: "#ef4444" }} />
          </div>
          <h4 className="mb-8">
            {estado === STATE.ERROR_PARAMS
              ? "Link inválido"
              : esErrorRed
                ? "Error de conexión"
                : "Link expirado o no válido"}
          </h4>
          <p className="text-secondary-light mb-24">{mensaje}</p>

          {esErrorRed ? (
            <button
              className="btn btn-outline-primary px-32 py-12 radius-12"
              onClick={() => window.location.reload()}
            >
              <Icon icon="mdi:refresh" width="20" className="me-2" />
              Reintentar
            </button>
          ) : (
            <Link to="/sign-up" className="btn btn-primary px-32 py-12 radius-12">
              Volver al registro
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default VerificarEmailLayer;
